import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './layout/Layout';

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string | null;
  duration_days: number;
  duration_type: string;
  price_inr: string;
  price_usd: string | null;
  subscription_type?: string;
  billing_period?: string | null;
  billing_period_count?: number | null;
  is_active: boolean;
  is_archived: boolean;
  is_popular: boolean;
  sort_order: number;
  effective_price_inr: number;
  has_discount: boolean;
  active_discount?: Discount;
  discounts?: Discount[];
  created_at: string;
}

interface Discount {
  id: number;
  plan_id: number;
  discount_type: string;
  discount_value: string;
  is_permanent: boolean;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
}

const SubscriptionPlans: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'active' | 'inactive' | 'archived'>('active');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Plan modal state
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    duration_type: 'monthly',
    duration_days: 30,
    price_inr: '',
    price_usd: '',
    coins_cost: '',
    remove_ads: false,
    full_features: false,
    is_active: true,
    sort_order: 0,
    subscription_type: 'recurring',
    billing_period: '',
    billing_period_count: 1
  });

  // Discount modal state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedPlanForDiscount, setSelectedPlanForDiscount] = useState<SubscriptionPlan | null>(null);
  const [editingDiscountId, setEditingDiscountId] = useState<number | null>(null);
  const [discountForm, setDiscountForm] = useState({
    discount_type: 'percentage',
    discount_value: '',
    is_permanent: true,
    start_date: '',
    end_date: ''
  });

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/admin/subscription-plans?status=${activeTab}`);
      if (response.data.success) {
        setPlans(response.data.data.plans);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleDurationTypeChange = (type: string) => {
    let days = planForm.duration_days;
    if (type === 'trial') days = 1;
    else if (type === 'weekly') days = 7;
    else if (type === 'monthly') days = 30;
    else if (type === 'yearly') days = 365;
    
    setPlanForm({ ...planForm, duration_type: type, duration_days: days });
  };

  const handleSavePlan = async () => {
    try {
      if (!planForm.name || !planForm.price_inr) {
        alert('Please fill in all required fields');
        return;
      }

      if (
        planForm.subscription_type === 'recurring' &&
        planForm.billing_period === 'daily' &&
        planForm.billing_period_count < 7
      ) {
        alert('Razorpay does not allow daily interval below 7. Use weekly or set Auto-Renew off.');
        return;
      }

      if (editingPlanId) {
        const response = await axios.put(`/admin/subscription-plans/${editingPlanId}`, planForm);
        if (response.data.success) {
          alert('Plan updated successfully');
          loadPlans();
          setShowPlanModal(false);
          setEditingPlanId(null);
          resetPlanForm();
        }
      } else {
        const response = await axios.post('/admin/subscription-plans', planForm);
        if (response.data.success) {
          alert('Plan created successfully');
          loadPlans();
          setShowPlanModal(false);
          resetPlanForm();
        }
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save plan');
    }
  };

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      name: plan.name,
      description: plan.description || '',
      duration_type: plan.duration_type,
      duration_days: plan.duration_days,
      price_inr: plan.price_inr,
      price_usd: plan.price_usd || '',
      coins_cost: (plan as any).coins_cost || '',
      remove_ads: (plan as any).remove_ads || false,
      full_features: (plan as any).full_features || false,
      is_active: plan.is_active,
      sort_order: plan.sort_order,
      subscription_type: plan.subscription_type || 'recurring',
      billing_period: plan.billing_period || '',
      billing_period_count: plan.billing_period_count || 1
    });
    setShowPlanModal(true);
  };

  const handleTogglePlan = async (id: number) => {
    try {
      await axios.patch(`/admin/subscription-plans/${id}/toggle`);
      loadPlans();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle plan');
    }
  };

  const handleArchivePlan = async (id: number) => {
    if (!window.confirm('Are you sure you want to archive this plan?')) {
      return;
    }

    try {
      await axios.patch(`/admin/subscription-plans/${id}/archive`);
      loadPlans();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to archive plan');
    }
  };

  const handleTogglePopular = async (id: number) => {
    try {
      await axios.patch(`/admin/subscription-plans/${id}/toggle-popular`);
      loadPlans();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle popular status');
    }
  };

  const handleDeletePlan = async (id: number) => {
    if (!window.confirm('Are you sure you want to permanently delete this plan? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/admin/subscription-plans/${id}`);
      loadPlans();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete plan');
    }
  };

  const handleAddDiscount = (plan: SubscriptionPlan) => {
    setSelectedPlanForDiscount(plan);
    setEditingDiscountId(null);
    setDiscountForm({
      discount_type: 'percentage',
      discount_value: '',
      is_permanent: true,
      start_date: '',
      end_date: ''
    });
    setShowDiscountModal(true);
  };

  const handleEditDiscount = (plan: SubscriptionPlan, discount: Discount) => {
    setSelectedPlanForDiscount(plan);
    setEditingDiscountId(discount.id);
    setDiscountForm({
      discount_type: discount.discount_type,
      discount_value: discount.discount_value,
      is_permanent: discount.is_permanent,
      start_date: discount.start_date || '',
      end_date: discount.end_date || ''
    });
    setShowDiscountModal(true);
  };

  const handleSaveDiscount = async () => {
    if (!selectedPlanForDiscount) return;

    try {
      if (!discountForm.discount_value) {
        alert('Please enter discount value');
        return;
      }

      // Prepare payload - convert empty strings to null for dates
      const payload = {
        ...discountForm,
        start_date: discountForm.start_date || null,
        end_date: discountForm.end_date || null
      };

      if (editingDiscountId) {
        // Update existing discount
        const response = await axios.put(
          `/admin/subscription-plans/${selectedPlanForDiscount.id}/discounts/${editingDiscountId}`,
          payload
        );
        if (response.data.success) {
          alert('Discount updated successfully');
          loadPlans();
          setShowDiscountModal(false);
          setSelectedPlanForDiscount(null);
          setEditingDiscountId(null);
        }
      } else {
        // Add new discount
        const response = await axios.post(
          `/admin/subscription-plans/${selectedPlanForDiscount.id}/discounts`,
          payload
        );
        if (response.data.success) {
          alert('Discount added successfully');
          loadPlans();
          setShowDiscountModal(false);
          setSelectedPlanForDiscount(null);
        }
      }
    } catch (error: any) {
      alert(error.response?.data?.message || `Failed to ${editingDiscountId ? 'update' : 'add'} discount`);
    }
  };

  const handleRemoveDiscount = async (planId: number, discountId: number) => {
    if (!window.confirm('Are you sure you want to remove this discount?')) {
      return;
    }

    try {
      await axios.delete(`/admin/subscription-plans/${planId}/discounts/${discountId}`);
      loadPlans();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to remove discount');
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      description: '',
      duration_type: 'monthly',
      duration_days: 30,
      price_inr: '',
      price_usd: '',
      coins_cost: '',
      remove_ads: false,
      full_features: false,
      is_active: true,
      sort_order: 0,
      subscription_type: 'recurring',
      billing_period: '',
      billing_period_count: 1
    });
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getDurationLabel = (days: number, type: string) => {
    if (type === 'trial') return `${days} Day Trial`;
    if (type === 'weekly') return 'Weekly';
    if (type === 'monthly') return 'Monthly';
    if (type === 'yearly') return 'Yearly';
    return `${days} Days`;
  };

  const getDiscountLabel = (discount: Discount) => {
    if (discount.discount_type === 'percentage') {
      return `${discount.discount_value}% OFF`;
    } else {
      return `₹${discount.discount_value} OFF`;
    }
  };

  const getDiscountExpiry = (discount: Discount) => {
    if (discount.is_permanent) return 'Permanent';
    if (!discount.end_date) return '';
    
    const endDate = new Date(discount.end_date);
    const now = new Date();
    const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return 'Expired';
    if (daysLeft === 0) return 'Expires today';
    if (daysLeft === 1) return 'Expires tomorrow';
    return `Ends in ${daysLeft} days`;
  };

  const calculateDiscountPreview = () => {
    if (!selectedPlanForDiscount || !discountForm.discount_value) return null;

    const originalPrice = parseFloat(selectedPlanForDiscount.price_inr);
    const discountValue = parseFloat(discountForm.discount_value);

    if (discountForm.discount_type === 'percentage') {
      return originalPrice * (1 - discountValue / 100);
    } else {
      return Math.max(0, originalPrice - discountValue);
    }
  };

  return (
    <Layout>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Subscription Plans</h4>
              <p className="text-muted mb-0">Manage subscription plans and pricing</p>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => {
                  resetPlanForm();
                  setEditingPlanId(null);
                  setShowPlanModal(true);
                }}
              >
                <i className="bx bx-plus-circle me-2"></i>
                New Plan
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
              onClick={() => setActiveTab('active')}
            >
              Active Plans
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'inactive' ? 'active' : ''}`}
              onClick={() => setActiveTab('inactive')}
            >
              Inactive Plans
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'archived' ? 'active' : ''}`}
              onClick={() => setActiveTab('archived')}
            >
              Archived Plans
            </button>
          </li>
        </ul>

        {/* Plans Grid */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
      </div>
        ) : plans.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bx bx-package font-size-48 text-muted mb-3"></i>
              <h5>No Plans Found</h5>
              <p className="text-muted">Create your first subscription plan to get started</p>
            </div>
          </div>
        ) : (
          <div className="row">
            {plans.map(plan => (
              <div key={plan.id} className="col-lg-4 col-md-6 mb-4">
                <div className="card h-100">
            <div className="card-body">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h5 className="card-title mb-1">{plan.name}</h5>
                        <div className="d-flex gap-2">
                          <span className="badge bg-info">{getDurationLabel(plan.duration_days, plan.duration_type)}</span>
                          {plan.is_popular && (
                            <span className="badge" style={{ backgroundColor: '#ff9800' }}>
                              <i className="bx bx-star me-1"></i>
                              MOST POPULAR
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`badge bg-${plan.is_active ? 'success' : plan.is_archived ? 'warning' : 'secondary'}`}>
                        {plan.is_archived ? 'Archived' : plan.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Description */}
                    {plan.description && (
                      <p className="text-muted small mb-3">{plan.description}</p>
                    )}

                    {/* Pricing */}
                    <div className="mb-3">
                      {plan.has_discount && plan.active_discount ? (
                        <div>
                          <div className="d-flex align-items-baseline gap-2 mb-2">
                            <h3 className="mb-0 text-success">{formatCurrency(plan.effective_price_inr)}</h3>
                            <h5 className="mb-0 text-muted text-decoration-line-through">{formatCurrency(plan.price_inr)}</h5>
                          </div>
                          <div>
                            <span className="badge bg-danger">
                              <i className="bx bx-purchase-tag me-1"></i>
                              {getDiscountLabel(plan.active_discount)}
                            </span>
                            <small className="text-muted ms-2">
                              {getDiscountExpiry(plan.active_discount)}
                            </small>
                          </div>
                        </div>
                      ) : (
                        <h3 className="mb-0">{formatCurrency(plan.price_inr)}</h3>
                      )}
                      
                      {/* Coin Cost */}
                      {(plan as any).coins_cost && (
                        <div className="mt-2">
                          <span className="badge" style={{ backgroundColor: '#ff9800' }}>
                            <i className="bx bx-coin me-1"></i>
                            {(plan as any).coins_cost} coins
                          </span>
                        </div>
                      )}
                      
                      {/* Features */}
                      <div className="mt-2 d-flex gap-1 flex-wrap">
                        {(plan as any).remove_ads && (
                          <span className="badge bg-info">
                            <i className="bx bx-block me-1"></i>
                            Ad-Free
                          </span>
                        )}
                        {(plan as any).full_features && (
                          <span className="badge bg-success">
                            <i className="bx bx-star me-1"></i>
                            Full Features
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEditPlan(plan)}
                      >
                        <i className="bx bx-edit me-1"></i>
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => handleAddDiscount(plan)}
                        disabled={plan.has_discount}
                      >
                        <i className="bx bx-purchase-tag me-1"></i>
                        {plan.has_discount ? 'Has Discount' : 'Add Discount'}
                      </button>
                      {!plan.is_archived && (
                        <>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleTogglePlan(plan.id)}
                          >
                            <i className={`bx bx-${plan.is_active ? 'pause' : 'play'} me-1`}></i>
                            {plan.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className={`btn btn-sm ${plan.is_popular ? 'btn-warning' : 'btn-outline-warning'}`}
                            onClick={() => handleTogglePopular(plan.id)}
                            title={plan.is_popular ? 'Remove Most Popular badge' : 'Mark as Most Popular'}
                          >
                            <i className="bx bx-star me-1"></i>
                            {plan.is_popular ? 'Popular' : 'Set Popular'}
                          </button>
                        </>
                      )}
                      {!plan.is_archived ? (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleArchivePlan(plan.id)}
                        >
                          <i className="bx bx-archive me-1"></i>
                          Archive
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeletePlan(plan.id)}
                        >
                          <i className="bx bx-trash me-1"></i>
                          Delete
                        </button>
                      )}
                    </div>

                    {/* Show active discount details */}
                    {plan.active_discount && (
                      <div className="mt-3 pt-3 border-top">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            <strong>Active Discount:</strong> {getDiscountLabel(plan.active_discount)}
                          </small>
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-link text-primary p-0"
                              onClick={() => handleEditDiscount(plan, plan.active_discount!)}
                              title="Edit Discount"
                            >
                              <i className="bx bx-edit"></i> Edit
                            </button>
                            <button
                              className="btn btn-sm btn-link text-danger p-0 ms-2"
                            onClick={() => handleRemoveDiscount(plan.id, plan.active_discount!.id)}
                              title="Remove Discount"
                            >
                              <i className="bx bx-x"></i> Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Plan Modal */}
        {showPlanModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingPlanId ? 'Edit' : 'Create'} Subscription Plan</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowPlanModal(false);
                      setEditingPlanId(null);
                      resetPlanForm();
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Plan Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={planForm.name}
                      onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                      placeholder="e.g., Premium Monthly"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={planForm.description}
                      onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                      placeholder="Optional plan description"
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Duration Type *</label>
                      <select
                        className="form-select"
                        value={planForm.duration_type}
                        onChange={(e) => handleDurationTypeChange(e.target.value)}
                      >
                        <option value="trial">Trial</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Duration (Days) *</label>
                      <input
                        type="number"
                        className="form-control"
                        value={planForm.duration_days}
                        onChange={(e) => setPlanForm({ ...planForm, duration_days: parseInt(e.target.value) || 1 })}
                        min="1"
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Auto-Renew</label>
                      <select
                        className="form-select"
                        value={planForm.subscription_type}
                        onChange={(e) => setPlanForm({ ...planForm, subscription_type: e.target.value })}
                      >
                        <option value="recurring">On (Recurring)</option>
                        <option value="one_time">Off (One-Time)</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Billing Period</label>
                      <select
                        className="form-select"
                        value={planForm.billing_period}
                        onChange={(e) => setPlanForm({ ...planForm, billing_period: e.target.value })}
                        disabled={planForm.subscription_type !== 'recurring'}
                      >
                        <option value="">Auto (use duration)</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                      <small className="text-muted">Daily requires interval ≥ 7</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Billing Interval</label>
                      <input
                        type="number"
                        className="form-control"
                        value={planForm.billing_period_count}
                        onChange={(e) => setPlanForm({ ...planForm, billing_period_count: parseInt(e.target.value) || 1 })}
                        min="1"
                        disabled={planForm.subscription_type !== 'recurring'}
                      />
                      <small className="text-muted">Number of periods per cycle</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Price in INR (₹) *</label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.price_inr}
                          onChange={(e) => setPlanForm({ ...planForm, price_inr: e.target.value })}
                          placeholder="999"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Price in USD ($) <small className="text-muted">(Optional)</small></label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.price_usd}
                          onChange={(e) => setPlanForm({ ...planForm, price_usd: e.target.value })}
                          placeholder="9.99"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Cost in Coins <small className="text-muted">(Optional)</small></label>
                    <div className="input-group">
                      <span className="input-group-text">💰</span>
                      <input
                        type="number"
                        className="form-control"
                        value={planForm.coins_cost}
                        onChange={(e) => setPlanForm({ ...planForm, coins_cost: e.target.value })}
                        placeholder="150"
                        min="0"
                      />
                    </div>
                    <small className="text-muted">Set how many coins required to unlock this plan</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label d-block mb-2">Plan Features</label>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="remove_ads"
                        checked={planForm.remove_ads}
                        onChange={(e) => setPlanForm({ ...planForm, remove_ads: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="remove_ads">
                        Remove Ads
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="full_features"
                        checked={planForm.full_features}
                        onChange={(e) => setPlanForm({ ...planForm, full_features: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="full_features">
                        Full Features Access (All premium features)
                      </label>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Sort Order</label>
                      <input
                        type="number"
                        className="form-control"
                        value={planForm.sort_order}
                        onChange={(e) => setPlanForm({ ...planForm, sort_order: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                      />
                      <small className="text-muted">Lower numbers appear first</small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={planForm.is_active}
                          onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })}
                        />
                        <label className="form-check-label">
                          {planForm.is_active ? 'Active' : 'Inactive'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPlanModal(false);
                      setEditingPlanId(null);
                      resetPlanForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSavePlan}
                    disabled={!planForm.name || !planForm.price_inr}
                  >
                    {editingPlanId ? 'Update Plan' : 'Create Plan'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Discount Modal */}
        {showDiscountModal && selectedPlanForDiscount && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingDiscountId ? 'Edit' : 'Add'} Discount to {selectedPlanForDiscount.name}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowDiscountModal(false);
                      setSelectedPlanForDiscount(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Discount Type *</label>
                    <select
                      className="form-select"
                      value={discountForm.discount_type}
                      onChange={(e) => setDiscountForm({ ...discountForm, discount_type: e.target.value, discount_value: '' })}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed_amount">Fixed Amount (₹)</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Discount Value *</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={discountForm.discount_value}
                        onChange={(e) => setDiscountForm({ ...discountForm, discount_value: e.target.value })}
                        placeholder={discountForm.discount_type === 'percentage' ? '25' : '250'}
                        min="0"
                        max={discountForm.discount_type === 'percentage' ? '100' : undefined}
                        step="0.01"
                      />
                      <span className="input-group-text">
                        {discountForm.discount_type === 'percentage' ? '%' : '₹'}
                      </span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={discountForm.is_permanent}
                        onChange={(e) => setDiscountForm({ ...discountForm, is_permanent: e.target.checked })}
                      />
                      <label className="form-check-label">
                        Permanent Discount
                      </label>
                    </div>
                  </div>

                  {!discountForm.is_permanent && (
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Start Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          value={discountForm.start_date}
                          onChange={(e) => setDiscountForm({ ...discountForm, start_date: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">End Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          value={discountForm.end_date}
                          onChange={(e) => setDiscountForm({ ...discountForm, end_date: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  {discountForm.discount_value && (
                    <div className="alert alert-info">
                      <strong>Preview:</strong>
                      <div className="mt-2">
                        Original: {formatCurrency(selectedPlanForDiscount.price_inr)}
                        <br />
                        Discounted: {formatCurrency(calculateDiscountPreview() || 0)}
                        <br />
                        <small className="text-muted">
                          Savings: {formatCurrency(parseFloat(selectedPlanForDiscount.price_inr) - (calculateDiscountPreview() || 0))}
                        </small>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDiscountModal(false);
                      setSelectedPlanForDiscount(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveDiscount}
                    disabled={!discountForm.discount_value}
                  >
                    {editingDiscountId ? 'Update Discount' : 'Add Discount'}
                  </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </Layout>
  );
};

export default SubscriptionPlans;
